import { useState, type FormEvent } from 'react';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../auth/AuthContext';

// Staff SSO: the email + password are the person's Amada login (amada-auth),
// the same one the other Amada dashboards use. This screen never sets a
// password; first-time and forgotten passwords go through an emailed link.
export function Login() {
  const { refresh } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [step, setStep] = useState<'signin' | 'forgot' | 'forgot_sent'>('signin');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onLogin = async (e: FormEvent) => {
    e.preventDefault();
    setError(null); setNotice(null); setBusy(true);
    try {
      await api('/api/auth/login', { method: 'POST', body: { email, password } });
      await refresh();
      window.location.href = '/';
    } catch (err) {
      const needsSetup = err instanceof ApiError
        && !!err.body && typeof err.body === 'object' && (err.body as { needsSetup?: unknown }).needsSetup === true;
      if (needsSetup) {
        setNotice((err as ApiError).message);
        setPassword('');
      } else {
        setError(err instanceof ApiError ? err.message : 'Sign-in failed');
      }
    } finally { setBusy(false); }
  };

  const onForgot = async (e: FormEvent) => {
    e.preventDefault();
    setError(null); setBusy(true);
    try {
      await api('/api/auth/forgot', { method: 'POST', body: { email } });
      setStep('forgot_sent');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally { setBusy(false); }
  };

  const linkBtn = { height: 'auto', padding: '4px 0', borderColor: 'transparent' } as const;

  return (
    <AuthShell>
      <h1 style={{ margin: '0 0 6px', fontSize: 22, fontWeight: 600, letterSpacing: '-0.015em' }}>
        {step === 'signin' ? 'Sign in' : 'Set or reset your password'}
      </h1>
      <div className="muted" style={{ fontSize: 13.5, marginBottom: 20 }}>
        {step === 'signin'
          ? 'Use your Amada login: the same email and password as the other Amada dashboards.'
          : 'We will email a link to set your Amada password.'}
      </div>

      {step === 'signin' && (
        <form onSubmit={onLogin} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <label className="form-lbl">Email</label>
          <input
            className="input"
            type="email"
            autoComplete="username"
            autoFocus
            required
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="you@amadafranchise.com"
          />
          <label className="form-lbl">Amada password</label>
          <input
            className="input"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={e => setPassword(e.target.value)}
          />
          {error && <ErrorBox text={error} />}
          {notice && <NoticeBox text={notice} />}
          <button className="btn btn-primary" disabled={busy} style={{ height: 36, marginTop: 6 }}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
          <div style={{ display: 'flex', marginTop: 4, fontSize: 12.5 }}>
            <div style={{ flex: 1 }} />
            <button type="button" className="btn btn-ghost" style={{ ...linkBtn, color: 'var(--accent)' }}
              onClick={() => { setStep('forgot'); setError(null); setNotice(null); }}>
              Forgot password?
            </button>
          </div>
        </form>
      )}

      {step === 'forgot' && (
        <form onSubmit={onForgot} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <label className="form-lbl">Email</label>
          <input
            className="input"
            type="email"
            autoComplete="username"
            autoFocus
            required
            value={email}
            onChange={e => setEmail(e.target.value)}
          />
          {error && <ErrorBox text={error} />}
          <button className="btn btn-primary" disabled={busy} style={{ height: 36, marginTop: 6 }}>
            {busy ? 'Sending…' : 'Email me a link'}
          </button>
          <div style={{ display: 'flex', marginTop: 4, fontSize: 12.5 }}>
            <button type="button" className="btn btn-ghost" style={linkBtn}
              onClick={() => { setStep('signin'); setError(null); }}>
              ← Back to sign in
            </button>
          </div>
        </form>
      )}

      {step === 'forgot_sent' && (
        <div>
          <div style={{ fontSize: 14, marginBottom: 10 }}>
            If <strong>{email}</strong> has access to this console, a link to set your Amada password is on its way.
          </div>
          <div className="muted" style={{ fontSize: 12.5 }}>
            The link expires in 1 hour. Check your spam folder if you don't see it. Still nothing? Contact Coastal.
          </div>
          <button className="btn" style={{ marginTop: 16 }} onClick={() => setStep('signin')}>Back to sign in</button>
        </div>
      )}
    </AuthShell>
  );
}

export function AuthShell({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 380 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8,
            background: 'linear-gradient(135deg, oklch(0.55 0.10 200), oklch(0.40 0.08 200))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, fontWeight: 700, color: '#fff', fontFamily: 'var(--font-mono)',
          }}>AFI</div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Encroachment Audit Console</div>
            <div className="muted" style={{ fontSize: 11.5 }}>Internal · Spec v0.6.0 · IL Pilot</div>
          </div>
        </div>
        <div className="card" style={{ padding: 28 }}>
          {children}
        </div>
      </div>
    </div>
  );
}

export function ErrorBox({ text }: { text: string }) {
  return (
    <div style={{
      padding: '8px 12px', background: 'var(--danger-soft)',
      border: '1px solid color-mix(in oklch, var(--danger) 30%, var(--border))',
      borderRadius: 6, color: 'var(--danger)', fontSize: 12.5,
    }}>{text}</div>
  );
}

export function NoticeBox({ text }: { text: string }) {
  return (
    <div style={{
      padding: '8px 12px', background: 'var(--accent-soft)',
      border: '1px solid color-mix(in oklch, var(--accent) 30%, var(--border))',
      borderRadius: 6, color: 'var(--text)', fontSize: 12.5, lineHeight: 1.5,
    }}>{text}</div>
  );
}
